import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { WindowSizeService } from 'common/window-size/window-size.service';
import { WindowSize, WindowSizeChangeEvent } from 'common/window-size/window-size';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-work-item-preview-lane-buttons',
  templateUrl: './work-item-preview-lane-buttons.component.html',
  styleUrls: ['./work-item-preview-lane-buttons.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WorkItemPreviewLaneButtonsComponent implements OnInit, OnDestroy {
  public isInfoPanelVisible: boolean = true;
  public isWorkLaneVisible: boolean = true;
  public isVisitorHistoryButtonVisible: boolean = true;
  public loaded = false;

  private _currentWindowSize: WindowSize;
  private _windowSizeSubscription: Subscription;

  constructor(private _rxEmitter: RxEmitter,
              private _changeDetector: ChangeDetectorRef,
              private _windowSizeService: WindowSizeService) {
  }

  ngOnInit(): void {
    this._windowSizeSubscription = this._windowSizeService.onResize$.subscribe(change => this._handleSizeChange(change));
  }

  ngOnDestroy(): void {
    if (this._windowSizeSubscription) {
      this._windowSizeSubscription.unsubscribe();
      this._windowSizeSubscription = null;
    }
  }

  public get isInfoButtonClickDisabled(): boolean {
    return this._shouldDisableButton(this.isInfoPanelVisible) ||
      (this.isInfoPanelVisible && !this.isWorkLaneVisible && !this.isVisitorHistoryButtonVisible);
  }

  public get isWorkLaneButtonClickDisabled(): boolean {
    return this._shouldDisableButton(this.isWorkLaneVisible) ||
      (this.isWorkLaneVisible && !this.isInfoPanelVisible && !this.isVisitorHistoryButtonVisible);
  }

  public get isVisitorHistoryButtonClickDisabled(): boolean {
    return this._shouldDisableButton(this.isVisitorHistoryButtonVisible) ||
      (this.isVisitorHistoryButtonVisible && !this.isWorkLaneVisible && !this.isInfoPanelVisible);
  }

  public onInfoButtonClick(): void {
    if (this.isInfoButtonClickDisabled) {
      return;
    }

    this.isInfoPanelVisible = !this.isInfoPanelVisible;
    this._rxEmitter.send(MessageBusEvents.PreviewInfoPanelVisibility, this.isInfoPanelVisible);
  }

  public onWorkLaneButtonClick(): void {
    if (this.isWorkLaneButtonClickDisabled) {
      return;
    }

    this.isWorkLaneVisible = !this.isWorkLaneVisible;
    this._rxEmitter.send(MessageBusEvents.PreviewWorkLaneVisibility, this.isWorkLaneVisible);
  }

  public onVisitorHistoryButtonClick(): void {
    if (this.isVisitorHistoryButtonClickDisabled) {
      return;
    }

    this.isVisitorHistoryButtonVisible = !this.isVisitorHistoryButtonVisible;
    this._rxEmitter.send(MessageBusEvents.PreviewVisitorHistoryVisibility, this.isVisitorHistoryButtonVisible);
  }

  private get _enabledButtonCount() {
    return [this.isInfoPanelVisible, this.isWorkLaneVisible, this.isVisitorHistoryButtonVisible]
      .filter(Boolean).length;
  }

  private _handleSizeChange(sizeChange: WindowSizeChangeEvent) {
    this._currentWindowSize = sizeChange.current;

    if (!this.loaded) {
      this.loaded = true;

      if (this._currentWindowSize !== WindowSize.LARGE) {
        this._hideVisitorHistory();
      }
    }

    if (this._shouldHideVisitorHistory(sizeChange)) {
      this._hideVisitorHistory();
    }

    this._changeDetector.detectChanges();
  }

  private _shouldDisableButton(buttonValue: boolean): boolean {
    return this._enabledButtonCount === 2 && !buttonValue && this._currentWindowSize !== WindowSize.LARGE;
  }

  private _shouldHideVisitorHistory(sizeChange: WindowSizeChangeEvent): boolean {
    const windowSizeDecreased = sizeChange.previous === WindowSize.LARGE &&
      (sizeChange.current === WindowSize.MEDIUM || sizeChange.current === WindowSize.SMALL);

    return windowSizeDecreased && this._enabledButtonCount === 3;
  }

  private _hideVisitorHistory(): void {
    this.isVisitorHistoryButtonVisible = false;
    this._rxEmitter.send(MessageBusEvents.PreviewVisitorHistoryVisibility, this.isVisitorHistoryButtonVisible);

    this._changeDetector.detectChanges();
  }
}
